import type { FC } from 'react';
import { useLayoutEffect, useRef, useState } from 'react';
import { Combobox, ComboboxContent, ComboboxInput } from '@components/ui/combobox';
import { Tooltip, TooltipContent, TooltipTrigger } from '@components/ui/tooltip';
import type { ResolvedSchemaTree } from '@root/helpers/schema/types';
import clsx from 'clsx';
import { motion } from 'motion/react';

import { APP_FOCUS_RING_OVERRIDE } from '../../constants';
import type { FieldDef, FieldItem } from '../../types';
import { CDR_SCHEMA } from '../../utils/cdr-schema';

import BlockClauseSelector from './BlockClauseSelector';
import ContractDetailsSelector from './ContractDetailsSelector';
import DataPointsSelector from './DataPointsSelector';
import FilterGroupSelector from './FilterGroupSelector';

// Maps each leaf datapoint id → its ancestor-title breadcrumb (e.g. "Period › From › Date").
// The trigger shows only the leaf name
const buildDatapointBreadcrumbs = (nodes: ResolvedSchemaTree[], ancestors: string[] = []): Map<string, string> => {
  const map = new Map<string, string>();
  for (const node of nodes) {
    const trail = [...ancestors, node.title];
    if (node.children?.length) {
      for (const [id, crumb] of buildDatapointBreadcrumbs(node.children as ResolvedSchemaTree[], trail)) {
        map.set(id, crumb);
      }
    } else {
      map.set(node.id, trail.join(' › '));
    }
  }
  return map;
};

const DATAPOINT_BREADCRUMBS = buildDatapointBreadcrumbs(CDR_SCHEMA.tree);
const DATAPOINT_ITEMS: FieldItem[] = CDR_SCHEMA.flatList.map((n) => ({ label: n.title, value: n.id }));

interface FieldSelectorProps {
  datapointTree: ResolvedSchemaTree[];
  contractDetailsFields: FieldDef[];
  blockFields: FieldDef[];
  onChange: (fieldId: string) => void;
  value: string;
}

export const FieldSelector: FC<FieldSelectorProps> = ({
  datapointTree,
  contractDetailsFields,
  blockFields,
  onChange,
  value,
}) => {
  const [open, setOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState<'contractDetails' | 'datapoints' | 'block-clause-details' | null>(
    () => {
      if (value.startsWith('blocks.')) {
        return 'block-clause-details';
      }
      if (
        value.startsWith('risk_details.') ||
        value.startsWith('security_details.') ||
        value.startsWith('subscription_agreement.') ||
        value.startsWith('fiscal_and_regulatory.') ||
        value.startsWith('broker_remuneration_and_deductions.')
      ) {
        return 'datapoints';
      }
      if (value) {
        return 'contractDetails';
      }
      return null;
    },
  );
  // Tracks the last non-null tab so the right panel keeps its content during the back-slide animation.
  const [displayedTab, setDisplayedTab] = useState<'contractDetails' | 'datapoints' | 'block-clause-details' | null>(
    selectedTab,
  );

  const contractDetailsItems: FieldItem[] = contractDetailsFields.map((f) => ({ label: f.label, value: f.id }));
  const blockItems: FieldItem[] = blockFields.map((f) => ({ label: f.label, value: f.id }));

  const allItems: FieldItem[] = [...contractDetailsItems, ...DATAPOINT_ITEMS, ...blockItems];
  const selectedItem = allItems.find((i) => i.value === value) ?? null;
  const selectedBreadcrumb = DATAPOINT_BREADCRUMBS.get(value);

  const [datapointSearch, setDatapointSearch] = useState('');
  const [blockSearch, setBlockSearch] = useState('');
  const [contractDetailsSearch, setContractDetailsSearch] = useState('');

  const filteredContractDetailsItems = contractDetailsItems.filter((f) =>
    f.label.toLowerCase().includes(contractDetailsSearch.toLowerCase()),
  );
  const filteredBlockItems = blockItems.filter((f) => f.label.toLowerCase().includes(blockSearch.toLowerCase()));

  const items =
    selectedTab === 'contractDetails'
      ? filteredContractDetailsItems
      : selectedTab === 'datapoints'
        ? DATAPOINT_ITEMS
        : filteredBlockItems;

  const handleDatapointSelect = (fieldId: string) => {
    onChange(fieldId);
    setOpen(false);
    setDatapointSearch('');
  };

  const leftPanelRef = useRef<HTMLDivElement>(null);
  const rightPanelRef = useRef<HTMLDivElement>(null);
  const [panelHeight, setPanelHeight] = useState<number | 'auto'>('auto');

  // Track the active panel's height so the popup can animate between menu and category sizes.
  useLayoutEffect(() => {
    if (!open) return;
    const active = selectedTab !== null ? rightPanelRef.current : leftPanelRef.current;
    if (!active) return;
    const measure = () => setPanelHeight(active.scrollHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(active);
    return () => observer.disconnect();
  }, [open, selectedTab]);

  const handleSetSelectedTab = (tab: 'contractDetails' | 'datapoints' | 'block-clause-details' | null) => {
    if (tab !== null) setDisplayedTab(tab);
    setSelectedTab(tab);
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);

    if (!isOpen) {
      setDatapointSearch('');
      setBlockSearch('');
      setContractDetailsSearch('');
      if (selectedTab === null) setDisplayedTab(null);
    }
  };

  return (
    <Combobox
      items={items}
      value={selectedItem}
      onValueChange={(item) => item && onChange(item.value)}
      open={open}
      onOpenChange={handleOpenChange}
    >
      {selectedBreadcrumb ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="w-full">
              <ComboboxInput
                className={clsx('border-info-300 h-10 w-full overflow-hidden shadow-none', APP_FOCUS_RING_OVERRIDE)}
                readOnly
                placeholder="Select a field"
              />
            </div>
          </TooltipTrigger>
          <TooltipContent className="max-w-sm">{selectedBreadcrumb}</TooltipContent>
        </Tooltip>
      ) : (
        <ComboboxInput
          className={clsx('border-info-300 h-10 w-full overflow-hidden shadow-none', APP_FOCUS_RING_OVERRIDE)}
          readOnly
          placeholder="Select a field"
        />
      )}

      <ComboboxContent className="flex max-h-[calc(var(--available-height)-50px)] flex-col overflow-clip p-0">
        <motion.div
          className="overflow-clip"
          initial={false}
          animate={{ height: panelHeight }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
        >
          <div
            data-testid="field-selector-slider"
            className={clsx(
              'flex w-[200%] flex-row transition-transform duration-300 ease-in-out',
              selectedTab !== null ? '-translate-x-1/2' : 'translate-x-0',
            )}
            onTransitionEnd={(e) => {
              // Drop the retained right-panel content once the back-slide finishes.
              if (e.target === e.currentTarget && selectedTab === null) setDisplayedTab(null);
            }}
          >
            <div ref={leftPanelRef} className="h-fit w-1/2">
              <FilterGroupSelector setSelectedTab={handleSetSelectedTab} />
            </div>
            <div ref={rightPanelRef} className="flex h-fit min-h-0 w-1/2 flex-col">
              {displayedTab === 'contractDetails' && (
                <ContractDetailsSelector
                  contractDetailsItems={filteredContractDetailsItems}
                  setSelectedTab={handleSetSelectedTab}
                  search={contractDetailsSearch}
                  setSearch={setContractDetailsSearch}
                />
              )}
              {displayedTab === 'datapoints' && (
                <DataPointsSelector
                  datapointTree={datapointTree}
                  datapointSearch={datapointSearch}
                  setDatapointSearch={setDatapointSearch}
                  value={value}
                  handleDatapointSelect={handleDatapointSelect}
                  setSelectedTab={handleSetSelectedTab}
                />
              )}
              {displayedTab === 'block-clause-details' && (
                <BlockClauseSelector
                  blockItems={filteredBlockItems}
                  setSelectedTab={handleSetSelectedTab}
                  search={blockSearch}
                  setSearch={setBlockSearch}
                />
              )}
            </div>
          </div>
        </motion.div>
      </ComboboxContent>
    </Combobox>
  );
};
